import { useId, type ReactNode } from 'react';
import { EntityLink } from 'components/entity-link';
import type { EntityAsFound, EntityAsFoundAddress } from 'types/api-responses';
import { EMPTY_CELL } from 'utils/empty-cell';
import { occurrenceKeys } from 'utils/occurrence-keys';
import styles from './document-card.module.scss';

const ADDRESS_LABELS: Record<EntityAsFoundAddress['kind'], string> = {
  standardized: 'Std Party Address',
  as_found: 'As Found Address',
};

// A document reached without an entity citation (transaction party, capacity,
// alias) still gets the cell's shape, every value an em dash.
const NO_APPEARANCE: EntityAsFound = {
  role: null,
  partyDetails: null,
  entityId: null,
  addresses: [],
};

/**
 * One label/value row of an appearance.
 * @param {object} props The row.
 * @param {string} props.label The field name.
 * @param {ReactNode} [props.value] The value, an em dash when blank.
 * @return {JSX.Element} The row.
 */
function Field({ label, value }: { label: string; value?: ReactNode }) {
  return (
    <div className={styles.entityRow}>
      <dt>{label}</dt>
      <dd>{value || EMPTY_CELL}</dd>
    </div>
  );
}

/**
 * How the viewed entity appears on this document: one block per party slot it
 * fills, each address labelled by whether it was standardized.
 * @param {object} props The column.
 * @param {EntityAsFound[]} props.appearances The entity's party appearances.
 * @return {JSX.Element} The column.
 */
export function EntityAsFoundColumn({
  appearances,
}: {
  appearances: EntityAsFound[];
}) {
  const headerId = useId();
  const blocks = appearances.length > 0 ? appearances : [NO_APPEARANCE];
  // Blocks carry no id and two slots can read the same, so each is keyed by its
  // slot and repeat.
  const keys = occurrenceKeys(
    blocks.map(
      ({ role, entityId, partyDetails }) =>
        `${role}|${entityId}|${partyDetails}`
    )
  );
  return (
    <div className={styles.entityCol} role="group" aria-labelledby={headerId}>
      <div id={headerId} className={styles.colHeader}>
        Entity as Found
      </div>
      {blocks.map((appearance, index) => (
        <dl key={keys[index]} className={styles.entityBlock}>
          <Field label="Role" value={appearance.role} />
          <Field
            label="Party Details"
            value={
              appearance.partyDetails?.trim() && (
                <EntityLink
                  id={appearance.entityId}
                  name={appearance.partyDetails}
                  hover
                />
              )
            }
          />
          {appearance.addresses.length > 0 ? (
            appearance.addresses.map((address) => (
              <Field
                key={address.id}
                label={ADDRESS_LABELS[address.kind]}
                value={address.label}
              />
            ))
          ) : (
            <Field label={ADDRESS_LABELS.as_found} />
          )}
        </dl>
      ))}
    </div>
  );
}
